import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';

type ProcessSummaryFocus = 'management' | 'structure' | 'fields';

const STEP_TYPES = new Set([
  'process',
  'flowchart-shape',
  'bpmn-shape',
  'decision',
  'terminator',
  'start-end',
  'data',
  'document',
]);

const DECISION_TYPES = new Set(['decision']);

const OWNER_FIELDS = ['owner', 'assignee', 'role', 'lane'];
const SYSTEM_FIELDS = ['system', 'systems', 'primarySystem', 'tool', 'application'];
const TIMING_FIELDS = ['duration', 'sla', 'slaTrigger', 'cycleTime'];
const RISK_FIELDS = ['risk', 'riskLevel', 'riskRefs', 'controlStatus'];

export const managementSummaryTool: IAgentTool = {
  name: 'process_management_summary',
  description:
    'Create a deterministic management-ready summary of the current process. Covers structure, owners, handoffs, timing/data readiness, and next actions.',
  parameters: {
    type: 'object',
    properties: {
      focus: {
        type: 'string',
        description: 'Summary focus: "management", "structure", or "fields".',
        enum: ['management', 'structure', 'fields'],
        default: 'management',
      },
    },
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'safe',
  category: 'query',

  async execute(params: Record<string, unknown>, context: IAgentToolContext) {
    const focus = normalizeFocus(params.focus);
    const analysis = analyzeProcess(context);

    return {
      success: true,
      summary: formatSummary(focus, analysis),
      data: {
        focus,
        stepCount: analysis.stepCount,
        decisionCount: analysis.decisionCount,
        ownerCount: analysis.ownerCount,
        systemCount: analysis.systemCount,
        timingCount: analysis.timingCount,
        riskCount: analysis.riskCount,
        disconnectedCount: analysis.disconnected.length,
        deadEndCount: analysis.deadEnds.length,
        handoffCount: analysis.handoffs.length,
      },
    };
  },
};

interface ProcessAnalysis {
  stepCount: number;
  decisionCount: number;
  edgeCount: number;
  ownerCount: number;
  systemCount: number;
  timingCount: number;
  riskCount: number;
  unassigned: string[];
  disconnected: string[];
  deadEnds: string[];
  starts: string[];
  endings: string[];
  handoffs: Array<{ from: string; to: string; count: number }>;
}

function normalizeFocus(value: unknown): ProcessSummaryFocus {
  if (value === 'structure' || value === 'fields') return value;
  return 'management';
}

function analyzeProcess(context: IAgentToolContext): ProcessAnalysis {
  const nodes = context.diagramData.nodes.filter((node) => STEP_TYPES.has(node.type));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = context.diagramData.edges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  const ownerByNode = new Map<string, string>();

  for (const edge of edges) {
    outgoing.set(edge.from, (outgoing.get(edge.from) ?? 0) + 1);
    incoming.set(edge.to, (incoming.get(edge.to) ?? 0) + 1);
  }

  const labels = new Map<string, string>();
  const unassigned: string[] = [];
  let ownerCount = 0;
  let systemCount = 0;
  let timingCount = 0;
  let riskCount = 0;
  let decisionCount = 0;

  for (const node of nodes) {
    const label = getNodeLabel(node);
    labels.set(node.id, label);

    if (isDecision(node)) decisionCount++;

    const owner = firstFieldValue(node, OWNER_FIELDS);
    if (owner) {
      ownerCount++;
      ownerByNode.set(node.id, owner);
    } else {
      unassigned.push(label);
    }

    if (firstFieldValue(node, SYSTEM_FIELDS)) systemCount++;
    if (firstFieldValue(node, TIMING_FIELDS)) timingCount++;
    if (firstFieldValue(node, RISK_FIELDS)) riskCount++;
  }

  const disconnected = nodes
    .filter((node) => (incoming.get(node.id) ?? 0) === 0 && (outgoing.get(node.id) ?? 0) === 0)
    .map((node) => labels.get(node.id) ?? node.id);
  const deadEnds = nodes
    .filter((node) => !isEndNode(node) && (incoming.get(node.id) ?? 0) > 0 && (outgoing.get(node.id) ?? 0) === 0)
    .map((node) => labels.get(node.id) ?? node.id);
  const starts = nodes
    .filter((node) => (incoming.get(node.id) ?? 0) === 0 && (outgoing.get(node.id) ?? 0) > 0)
    .map((node) => labels.get(node.id) ?? node.id);
  const endings = nodes
    .filter((node) => (incoming.get(node.id) ?? 0) > 0 && (outgoing.get(node.id) ?? 0) === 0)
    .map((node) => labels.get(node.id) ?? node.id);

  const handoffCounts = new Map<string, { from: string; to: string; count: number }>();
  for (const edge of edges) {
    const from = ownerByNode.get(edge.from);
    const to = ownerByNode.get(edge.to);
    if (!from || !to || from === to) continue;
    const key = `${from}\u0000${to}`;
    const current = handoffCounts.get(key) ?? { from, to, count: 0 };
    current.count++;
    handoffCounts.set(key, current);
  }

  return {
    stepCount: nodes.length,
    decisionCount,
    edgeCount: edges.length,
    ownerCount,
    systemCount,
    timingCount,
    riskCount,
    unassigned,
    disconnected,
    deadEnds,
    starts,
    endings,
    handoffs: Array.from(handoffCounts.values()).sort((a, b) => b.count - a.count),
  };
}

function formatSummary(focus: ProcessSummaryFocus, analysis: ProcessAnalysis): string {
  if (analysis.stepCount === 0) {
    return 'No process steps found yet. Add or draft a process before running management analysis.';
  }

  if (focus === 'structure') {
    return [
      `Structure scan: ${analysis.stepCount} step(s), ${analysis.decisionCount} decision(s), ${analysis.edgeCount} connector(s).`,
      formatStructureFindings(analysis),
      formatNextActions(analysis, ['Resolve disconnected or dead-end steps.', 'Confirm each decision has labelled outcomes.', 'Tidy the layout after structural fixes.']),
    ].join('\n\n');
  }

  if (focus === 'fields') {
    return [
      `Field readiness: owners ${analysis.ownerCount}/${analysis.stepCount}, systems ${analysis.systemCount}/${analysis.stepCount}, timing/SLA ${analysis.timingCount}/${analysis.stepCount}, risk/control ${analysis.riskCount}/${analysis.stepCount}.`,
      formatMissingFieldGuidance(analysis),
      formatNextActions(analysis, ['Add owner or lane for each work step.', 'Add system/dependency and SLA or duration fields for analysis.', 'Add risk/control fields before compliance or audit review.']),
    ].join('\n\n');
  }

  return [
    `Management summary: ${analysis.stepCount} step(s), ${analysis.decisionCount} decision(s), ${analysis.edgeCount} connector(s).`,
    formatStructureFindings(analysis),
    formatReadiness(analysis),
    formatHandoffs(analysis),
    formatNextActions(analysis, ['Assign owners to unassigned steps.', 'Add timing/SLA fields before calling bottlenecks final.', 'Add systems and risks to make handoff and risk review reliable.']),
  ].join('\n\n');
}

function formatStructureFindings(analysis: ProcessAnalysis): string {
  const findings: string[] = [];

  if (analysis.disconnected.length > 0) {
    findings.push(`Disconnected: ${formatList(analysis.disconnected)}.`);
  }
  if (analysis.deadEnds.length > 0) {
    findings.push(`Potential dead ends: ${formatList(analysis.deadEnds)}.`);
  }
  if (analysis.starts.length === 0) {
    findings.push('No clear entry step was detected.');
  } else if (analysis.starts.length > 1) {
    findings.push(`Multiple possible entry steps: ${formatList(analysis.starts)}.`);
  }
  if (analysis.endings.length === 0) {
    findings.push('No clear ending step was detected.');
  }

  return findings.length > 0
    ? `Structure findings:\n${findings.map((finding) => `- ${finding}`).join('\n')}`
    : 'Structure findings:\n- The process has connected flow structure with a detectable entry and ending.';
}

function formatReadiness(analysis: ProcessAnalysis): string {
  return [
    'Analysis readiness:',
    `- Owners: ${analysis.ownerCount}/${analysis.stepCount} step(s) populated.`,
    `- Systems/dependencies: ${analysis.systemCount}/${analysis.stepCount} step(s) populated.`,
    `- Timing/SLA: ${analysis.timingCount}/${analysis.stepCount} step(s) populated.`,
    `- Risk/control: ${analysis.riskCount}/${analysis.stepCount} step(s) populated.`,
  ].join('\n');
}

function formatHandoffs(analysis: ProcessAnalysis): string {
  if (analysis.ownerCount === 0) {
    return 'Handoffs: owner or lane fields are missing, so risky handoffs cannot be verified yet.';
  }
  if (analysis.handoffs.length === 0) {
    return 'Handoffs: no cross-owner handoffs detected from the available owner fields.';
  }

  return `Handoffs:\n${analysis.handoffs
    .slice(0, 5)
    .map((handoff) => `- ${handoff.from} to ${handoff.to}: ${handoff.count} connector(s)`)
    .join('\n')}`;
}

function formatMissingFieldGuidance(analysis: ProcessAnalysis): string {
  const missing: string[] = [];
  if (analysis.ownerCount < analysis.stepCount) missing.push('owner or lane');
  if (analysis.systemCount < analysis.stepCount) missing.push('system or dependency');
  if (analysis.timingCount < analysis.stepCount) missing.push('duration or SLA');
  if (analysis.riskCount < analysis.stepCount) missing.push('risk or control status');

  if (missing.length === 0) {
    return 'The core improvement fields are present on every step. You can run bottleneck, handoff, and risk analysis.';
  }

  return `Missing fields to add before deeper improvement analysis: ${missing.join(', ')}. Start with the highest-volume or most customer-visible steps.`;
}

function formatNextActions(analysis: ProcessAnalysis, defaults: string[]): string {
  const actions: string[] = [];
  if (analysis.unassigned.length > 0) {
    actions.push(`Assign owners for: ${formatList(analysis.unassigned)}.`);
  }
  if (analysis.disconnected.length > 0) {
    actions.push(`Connect or remove disconnected steps: ${formatList(analysis.disconnected)}.`);
  }
  if (analysis.deadEnds.length > 0) {
    actions.push(`Confirm outcomes for dead-end steps: ${formatList(analysis.deadEnds)}.`);
  }

  for (const fallback of defaults) {
    if (actions.length >= 3) break;
    actions.push(fallback);
  }

  return `Next actions:\n${actions.slice(0, 3).map((action, index) => `${index + 1}. ${action}`).join('\n')}`;
}

function formatList(items: string[], limit = 5): string {
  const visible = items.slice(0, limit);
  const suffix = items.length > limit ? ` and ${items.length - limit} more` : '';
  return `${visible.join(', ')}${suffix}`;
}

function getNodeLabel(node: IAgentToolContext['diagramData']['nodes'][number]): string {
  const raw =
    node.attrs?.label ??
    node.props?.label ??
    node.name ??
    node.attrs?.name ??
    node.props?.name ??
    node.id;
  return String(raw);
}

function firstFieldValue(
  node: IAgentToolContext['diagramData']['nodes'][number],
  fields: readonly string[],
): string | null {
  for (const field of fields) {
    const value = node.attrs?.[field] ?? node.props?.[field];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value) && value.length > 0) return String(value[0]);
  }
  return null;
}

function isDecision(node: IAgentToolContext['diagramData']['nodes'][number]): boolean {
  const variant = String(node.attrs?.variant ?? node.props?.variant ?? '').toLowerCase();
  const stepKind = String(node.attrs?.stepKind ?? node.props?.stepKind ?? '').toLowerCase();
  return DECISION_TYPES.has(node.type) || variant.includes('decision') || stepKind.includes('decision') || stepKind.includes('gateway');
}

function isEndNode(node: IAgentToolContext['diagramData']['nodes'][number]): boolean {
  const terminatorType = String(node.attrs?.terminatorType ?? node.props?.terminatorType ?? '').toLowerCase();
  const stepKind = String(node.attrs?.stepKind ?? node.props?.stepKind ?? '').toLowerCase();
  const label = getNodeLabel(node).toLowerCase();
  return terminatorType === 'end' || stepKind === 'end' || label === 'done' || label === 'end';
}
